import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAvailabilityResponse, ICalendarAvailabilityResponse, IEventSite, IEventSitesResponse, IQueryOptionsResponse, IReservationResponse, ITableReservationPayload, ITimeBasedEvent, ITimeBasedEventsSearchResponse } from '../interfaces/interfaces';
import { SettingsService } from 'core-lib';

@Injectable({ providedIn: 'root' })
export class EventSiteService {

  constructor(
    private http: HttpClient,
    private settings: SettingsService,
    @Inject('environment') private environment: any
  ) {}

  getEventSites(): Observable<IEventSite[]> {
    const endpoints = this.settings.get('app.endpoints');
    const hotelId = endpoints?.[0]?.hotelCode;
    return this.http.get<IEventSitesResponse>(
      `${this.environment.backendHost}/api/eventmanager/sites?hotelId=${hotelId}`
    ).pipe(map(response => response.data));
  }

  /** Returns all time-based event definitions configured for the hotel, regardless of date or status. */
  searchTimeBasedEvents(): Observable<ITimeBasedEvent[]> {
    const endpoints = this.settings.get('app.endpoints');
    const hotelId = endpoints?.[0]?.hotelCode;
    const url = `${this.environment.backendHost}/api/eventmanager/timebasedevents/search`;
    const params = new HttpParams().set('HotelId', hotelId);
    return this.http.get<ITimeBasedEventsSearchResponse>(url, { params }).pipe(map(response => response.data));
  }

  getQueryOptions(eventSiteCode: string, date: string): Observable<IQueryOptionsResponse> {
    const endpoints = this.settings.get('app.endpoints');
    const hotelId = endpoints?.[0]?.hotelCode;
    const url = `${this.environment.backendHost}/api/eventmanager/availability/query-options`;
    const params = new HttpParams()
      .set('HotelId', hotelId)
      .set('EventSiteId', eventSiteCode)
      .set('HotelDate', date);
    return this.http.get<IQueryOptionsResponse>(url, { params });
  }

  getCalendarAvailability(
    fromHotelDate: string,
    toHotelDate: string,
    eventSiteId?: string,
    eventSiteCategoryId?: string
  ): Observable<ICalendarAvailabilityResponse> {
    const endpoints = this.settings.get('app.endpoints');
    const hotelId = endpoints?.[0]?.hotelCode;
    const url = `${this.environment.backendHost}/api/eventmanager/availability/overview`;
    let params = new HttpParams()
      .set('HotelId', hotelId)
      .set('FromHotelDate', fromHotelDate)
      .set('ToHotelDate', toHotelDate);
    if (eventSiteCategoryId) {
      params = params.set('EventSiteCategoryId', eventSiteCategoryId);
    }
    if (eventSiteId) {
      params = params.set('EventSiteId', eventSiteId);
    }
    return this.http.get<ICalendarAvailabilityResponse>(url, { params });
  }

  getAvailability(eventSiteCode: string, date: string, guestCount: number): Observable<IAvailabilityResponse> {
    const endpoints = this.settings.get('app.endpoints');
    const hotelId = endpoints?.[0]?.hotelCode;
    const url = `${this.environment.backendHost}/api/eventmanager/availability/query`;
    const params = new HttpParams()
      .set('HotelId', hotelId)
      .set('EventSiteId', eventSiteCode)
      .set('Units', guestCount.toString())
      .set('HotelDate', date);
    return this.http.get<IAvailabilityResponse>(url, { params });
  }

  submitReservation(payload: ITableReservationPayload): Observable<IReservationResponse> {
    return this.http.post<IReservationResponse>(
      `${this.environment.backendHost}/api/Reservations/table`,
      payload
    );
  }
}
